/**
 * V2ConditionValidator
 *
 * Validates individual condition rows and complete editor state.
 * Uses variable metadata for type-aware validation.
 *
 * @module v2-condition-editor
 */

import type { V2ConditionRow, V2DataSource, V2ValidationResult } from '../types/v2-condition-editor.types';
import { V2_VALIDATION_MESSAGES } from '../constants/v2-condition-constants';
import { variableOperatorService } from '@modules/v2-variable-utils/services/VariableOperatorService';

export class V2ConditionValidator {
  /**
   * Validate a single condition row against available data sources.
   *
   * Checks:
   * 1. Variable (key) is selected
   * 2. Variable exists in data sources
   * 3. Operator is selected
   * 4. Operator is valid for variable type
   * 5. Value is present (unless exists/notExists)
   * 6. Value is valid for the variable type (number format, etc.)
   *
   * @param row - Condition row to validate
   * @param dataSources - Available data sources
   * @returns Validation result
   */
  validateRow(
    row: V2ConditionRow,
    dataSources: V2DataSource[],
  ): V2ValidationResult {
    // 1. Variable must be selected
    if (!row.key) {
      return { isValid: false, error: V2_VALIDATION_MESSAGES.VARIABLE_REQUIRED };
    }

    // 2. Variable must exist in data sources
    const source = dataSources.find((ds) => ds.variableId === row.key);
    if (!source) {
      return { isValid: false, error: V2_VALIDATION_MESSAGES.VARIABLE_NOT_FOUND };
    }

    // 3. Operator must be selected
    if (!row.operator) {
      return { isValid: false, error: V2_VALIDATION_MESSAGES.OPERATOR_REQUIRED };
    }

    // 4. Operator must be valid for variable type
    if (!variableOperatorService.isOperatorValid(row.operator, source.variableType)) {
      return { isValid: false, error: V2_VALIDATION_MESSAGES.INVALID_OPERATOR };
    }

    // 5. Value required (unless exists/notExists)
    if (variableOperatorService.operatorRequiresValue(row.operator)) {
      if (!row.value && row.value !== '0') {
        return { isValid: false, error: V2_VALIDATION_MESSAGES.VALUE_REQUIRED };
      }

      // 6. Type-specific value validation
      const typeError = this.validateValueForType(row.value, source.variableType);
      if (typeError) {
        return { isValid: false, error: typeError };
      }
    }

    return { isValid: true };
  }

  /**
   * Validate all rows in a condition editor state.
   * Returns true only if ALL rows are valid.
   */
  validateAll(
    rows: V2ConditionRow[],
    dataSources: V2DataSource[],
  ): { isValid: boolean; results: Map<string, V2ValidationResult> } {
    const results = new Map<string, V2ValidationResult>();
    let allValid = true;

    for (const row of rows) {
      const result = this.validateRow(row, dataSources);
      results.set(row.id, result);
      if (!result.isValid) allValid = false;
    }

    return { isValid: allValid, results };
  }

  /**
   * Type-specific value validation.
   */
  private validateValueForType(
    value: string,
    variableType: string,
  ): string | null {
    switch (variableType) {
      case 'number': {
        // Allow comma-separated for in/notIn
        const values = value.split(',').map((v) => v.trim());
        for (const v of values) {
          if (v && isNaN(Number(v))) {
            return V2_VALIDATION_MESSAGES.INVALID_NUMBER;
          }
        }
        return null;
      }

      case 'date': {
        const values = value.split(',').map((v) => v.trim());
        for (const v of values) {
          if (v && isNaN(Date.parse(v))) {
            return V2_VALIDATION_MESSAGES.INVALID_DATE;
          }
        }
        return null;
      }

      default:
        return null;
    }
  }
}

/** Singleton instance */
export const v2ConditionValidator = new V2ConditionValidator();
